"use client";

import React from "react";
import { ChevronRight, TypeIcon as type, type LucideIcon } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { Link, useNavigate } from "react-router";

export interface NavItem {
  title: string;
  url: string;
  icon?: LucideIcon;
  subItems?: NavItem[];
  disabled?: boolean;
  separatorBefore?: boolean;
}

export interface NavGroup {
  title: string;
  icon?: LucideIcon;
  items: NavItem[];
  collapsible?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
}

interface NavMainProps {
  items: NavGroup[];
}

export function NavMain({ items }: NavMainProps) {
  const [openGroups, setOpenGroups] = React.useState<string[]>([]);
  const [openSubItems, setOpenSubItems] = React.useState<string[]>([]);

  const navigate = useNavigate();

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) =>
      prev.includes(title)
        ? prev.filter((group) => group !== title)
        : [...prev, title]
    );
  };

  const toggleSubItem = (title: string) => {
    setOpenSubItems((prev) =>
      prev.includes(title)
        ? prev.filter((item) => item !== title)
        : [...prev, title]
    );
  };

  const renderNavItem = (item: NavItem, isSubItem = false) => {
    const itemKey = item.title;

    if (item.subItems?.length) {
      return (
        <SidebarMenuItem key={itemKey}>
          <Collapsible
            open={openSubItems.includes(item.title)}
            onOpenChange={() => toggleSubItem(item.title)}
            disabled={item.disabled}
          >
            <CollapsibleTrigger asChild>
              <SidebarMenuButton disabled={item.disabled}>
                {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                <span>{item.title}</span>
                <ChevronRight
                  className={`ml-auto h-4 w-4 transition-transform duration-200 ${
                    openSubItems.includes(item.title) ? "rotate-90" : ""
                  }`}
                />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {item.subItems.map((subItem) => renderNavItem(subItem, true))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </Collapsible>
        </SidebarMenuItem>
      );
    }

    if (isSubItem) {
      return (
        <SidebarMenuSubItem key={itemKey}>
          <SidebarMenuSubButton
            aria-disabled={item.disabled || undefined}
            className={item.disabled ? "pointer-events-none opacity-50" : undefined}
            onClick={() => {
              if (item.disabled || !item.url) {
                return;
              }
              navigate(item.url);
            }}
          >
            {item.icon && <item.icon className="h-4 w-4" />}
            <span>{item.title}</span>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      );
    }

    return (
      <SidebarMenuItem key={itemKey}>
        <SidebarMenuButton
          disabled={item.disabled}
          onClick={() => {
            if (item.url) {
              navigate(item.url);
            }
          }}
        >
          {item.icon && <item.icon className="mr-2 h-4 w-4" />}
          <span>{item.title}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  const renderGroupItems = (groupItems: NavItem[]) =>
    groupItems.map((item) => (
      <React.Fragment key={item.title}>
        {item.separatorBefore ? (
          <SidebarMenuItem className="pointer-events-none py-0.5">
            <SidebarSeparator className="group-data-[collapsible=icon]:hidden" />
          </SidebarMenuItem>
        ) : null}
        {renderNavItem(item)}
      </React.Fragment>
    ));

  return (
    <>
      {items.map((group) => (
        <SidebarGroup key={group.title}>
          {!group.hideLabel && (
            <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
          )}
          <SidebarMenu>
            {group.collapsible ? (
              <Collapsible
                open={openGroups.includes(group.title)}
                onOpenChange={() => toggleGroup(group.title)}
              >
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton disabled={group.disabled}>
                    {group.icon && <group.icon className="mr-2 h-4 w-4" />}
                    <span>{group.title}</span>
                    <ChevronRight
                      className={`ml-auto h-4 w-4 transition-transform duration-200 ${
                        openGroups.includes(group.title) ? "rotate-90" : ""
                      }`}
                    />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {renderGroupItems(group.items)}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </Collapsible>
            ) : (
              renderGroupItems(group.items)
            )}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  );
}
